import { GALLERY_TEMPLATE_RIGHT } from "csi/constants";
import CustomViewOptionCustomSection from "csi/middleware/customView/options/CustomViewOptionCustomSection";
import { IMAGE_SIZE_CUSTOM } from "csi/model/image";
import { isDefined } from "csi/utility/coreUtility";
import { Box, FlexColumn, FlexRow, OverflowText, SingleSelect, TextField } from "designSystem/index";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import { useCustomViewDialogStore } from "../CustomViewDialogStore";
import CheckboxOption from "./CheckboxOption";
import css from "./CustomViewOptions.module.css";
import FilterOptions from "./FilterOptions";
import GalleryCustomViewOptionsStore from "./GalleryCustomViewOptionsStore";
import GalleryPrintOptions from "./GalleryPrintOptions";
import SelectOption from "./SelectOption";

const GalleryOptionsTab: FunctionComponent = observer(() => {
	const { optionsStore } = useCustomViewDialogStore();

	const {
		disableQuerySize,
		displayGalleryAttributeWidth,
		displayGalleryImageHeight,
		displayGalleryImageWidth,
		displayGroupHeaderWidth,
		galleryAttributeWidth,
		galleryImageHeight,
		galleryImageWidth,
		galleryAttributeWidthValidationMsg,
		galleryImageHeightValidationMsg,
		galleryImageWidthValidationMsg,
		galleryDropDownOptions,
		isGroupHeaderWidthDisabled,
		groupHeaderWidth,
		groupHeaderWidthValidationMsg,
		hasGroupHeaderWidthError,
		hasGalleryAttributeWidthError,
		hasGalleryImageHeightError,
		hasGalleryImageWidthError,
		hasPrintOptions,
		onChangeAttributeWidth,
		onChangeGroupHeaderWidth,
		options,
		printOptionsCollapse,
		selectedGalleryImageLabel,
		selectedGalleryTemplate,
		selectedGroupItemsPerRow,
		selectedImageSizes,
		selectedQuerySize,
		setCardTemplate,
		setGroupItemsPerRow,
		setImageHeight,
		setImageLabel,
		setImageSize,
		setImageWidth,
		setQuerySize,
		setShowFileSizes,
		toggleCollapsedPrintOptions,
		toggleCollapsedViewMainOptions,
		viewMainOptionsCollapse,
	} = optionsStore as GalleryCustomViewOptionsStore;

	const {
		cardTemplates,
		groupItemsPerRowOptions,
		imageLabels,
		imageSizes,
		resultCounts,
	} = galleryDropDownOptions;

	const {
		showFileSizes,
	} = options;

	return (
		<FlexColumn data-csi-automation="GalleryOptionsTab">
			{/* gallery options */}
			<CustomViewOptionCustomSection
				collapsed={viewMainOptionsCollapse}
				onCollapse={toggleCollapsedViewMainOptions}
				titleRes="CustomViewGalleryOptions"
			>
				<FlexRow className={css.container}>
					<OverflowText
						className={css.sectionTitle}
						res="CustomViewDisplayOptionsAppearance"
					/>
				</FlexRow>
				{isDefined(selectedGalleryTemplate) && (
					<SelectOption
						dataCsiAutomation="CustomViewGalleryCardTemplate"
						res="CustomViewGalleryCardTemplate"
						selectProps={{
							onChange: setCardTemplate,
							options: cardTemplates,
							value: selectedGalleryTemplate,
						}}
					/>
				)}
				{isDefined(selectedImageSizes) && (
					<Box 
						className={css.galleryContainer}
						data-csi-automation="CustomViewImageSelectedImagesSizes"
					>
						<FlexRow>
							<FlexColumn className={css.optionLeft}>
								<SingleSelect
									disableClearable
									fullWidth
									labelRes="CustomViewImageSize"
									onChange={setImageSize}
									options={imageSizes}
									value={selectedImageSizes}
								/>
							</FlexColumn>
						</FlexRow>
						{(selectedImageSizes.id === IMAGE_SIZE_CUSTOM) && (
							<FlexRow>
								{isDefined(galleryImageWidth) && (
									<FlexColumn className={css.optionLeft} data-csi-automation="CustomViewImageWidthPX">
										<TextField
											error={!!hasGalleryImageWidthError}
											fullWidth
											helperText={galleryImageWidthValidationMsg}
											onChange={setImageWidth}
											res="CustomViewImageWidthPX"
											value={displayGalleryImageWidth}
										/>
									</FlexColumn>
								)}
								{isDefined(galleryImageHeight) && (
									<FlexColumn className={css.optionRightSelect} data-csi-automation="CustomViewImageHeightPX">
										<TextField
											error={!!hasGalleryImageHeightError}
											fullWidth
											helperText={galleryImageHeightValidationMsg}
											onChange={setImageHeight}
											res="CustomViewImageHeightPX"
											value={displayGalleryImageHeight}
										/>
									</FlexColumn>
								)}
							</FlexRow>
						)}
					</Box>
				)}
				{isDefined(galleryAttributeWidth) && selectedGalleryTemplate?.id === GALLERY_TEMPLATE_RIGHT && (
					<Box 
						className={css.galleryContainer}
						data-csi-automation="CustomViewGalleryAttributesWidth"
					>
						<FlexColumn className={css.optionLeft}>
							<TextField
								error={!!hasGalleryAttributeWidthError}
								fullWidth
								helperText={galleryAttributeWidthValidationMsg}
								onChange={onChangeAttributeWidth}
								placeholderRes="ViewPreferences_DefaultValue"
								res="CustomViewGalleryAttributesWidth"
								value={displayGalleryAttributeWidth}
							/>
						</FlexColumn>
					</Box>
				)}
				{isDefined(selectedGalleryImageLabel) && (
					<SelectOption
						dataCsiAutomation="CustomViewGalleryCoverImage"
						res="CustomViewGalleryCoverImage"
						selectProps={{
							onChange: setImageLabel,
							options: imageLabels,
							value: selectedGalleryImageLabel,
						}}
					/>
				)}
				{isDefined(selectedQuerySize) && (
					<SelectOption
						dataCsiAutomation="CustomViewResultCount"
						res="CustomViewResultCount"
						selectProps={{
							disabled: disableQuerySize,
							onChange: setQuerySize,
							options: resultCounts,
							value: selectedQuerySize,
						}}
					/>
				)}
				<Box className={css.galleryContainer}>
					<FlexRow>
						{isDefined(selectedGroupItemsPerRow) && (
							<FlexColumn 
								className={css.optionLeft}
								data-csi-automation="CustomViewGalleryItemsInRowPerGroup"
							>
								<SingleSelect
									disableClearable
									fullWidth
									labelRes="CustomViewGalleryItemsInRowPerGroup"
									onChange={setGroupItemsPerRow}
									options={groupItemsPerRowOptions}
									value={selectedGroupItemsPerRow}
								/>
							</FlexColumn>
						)}
						{isDefined(groupHeaderWidth) && (
							<FlexColumn 
								className={isDefined(selectedGroupItemsPerRow) ? css.optionRightSelect : css.optionLeft}
								data-csi-automation="CustomViewGalleryGroupsRowHeaderWidth"
							>
								<TextField
									disabled={isGroupHeaderWidthDisabled}
									error={!!hasGroupHeaderWidthError}
									fullWidth
									helperText={groupHeaderWidthValidationMsg}
									onChange={onChangeGroupHeaderWidth}
									placeholderRes="ViewPreferences_DefaultValue"
									res="CustomViewGalleryGroupsRowHeaderWidth"
									value={displayGroupHeaderWidth}
								/>
							</FlexColumn>
						)}
					</FlexRow>
				</Box>
				<FlexRow className={css.container}>
					<OverflowText
						className={css.sectionTitle}
						res="RuleFilters"
					/>
				</FlexRow>
				<FilterOptions />
				<FlexRow className={css.container}>
					<OverflowText
						className={css.sectionTitle}
						res="CustomViewAdvancedOptions"
					/>
				</FlexRow>
				<CheckboxOption
					dataCsiAutomation="CustomViewShowFileSizes"
					label="CustomViewShowFileSizes"
					onChange={setShowFileSizes}
					optionValue={showFileSizes}
					paddingLeft={0}
				/>
			</CustomViewOptionCustomSection>
			{hasPrintOptions && (
				<CustomViewOptionCustomSection
					collapsed={printOptionsCollapse}
					dataCsiAutomation="GalleryPrintOptions"
					onCollapse={toggleCollapsedPrintOptions}
					titleRes="CustomViewPrintOptions"
				>
					<GalleryPrintOptions />
				</CustomViewOptionCustomSection>
			)}
		</FlexColumn>
	);
});

export = GalleryOptionsTab;